class CountrySelector extends HTMLElement {
  connectedCallback() {
    this.selectors = {
      selectedCountry: '.country-selector__selected-country',
      modal: '.country-selector__modal',
      modalInner: '.country-selector__modal-inner',
      modalClose: '.country-selector__modal-close',
      countryLabel: '.country-selector__label',
      countryList: '.country-selector__modal-country-list',
      countryItem: '.country-selector__modal-country a',
      search: '.country-selector__modal-search',
      countryForm: '#HeaderCountryForm',
    };

    this.fallbackCountryCode = 'US';

    this.selectedCountry = this.querySelector(this.selectors.selectedCountry);
    this.countryForm = document.querySelector(this.selectors.countryForm);
    this.countryFormSelect = this.countryForm.querySelector('#country-select');
    this.availableCountries = Array.from(this.countryFormSelect?.querySelectorAll('option')).map(
      option => option.value,
    );

    this.currentCountry.voltage ? this.init() : this.countryIdentification();

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.addEventListener('click', e => {
      if (e.target.closest(this.selectors.countryLabel)) {
        this.openModal();
      }
    });

    document.addEventListener('click', e => {
      if (
        e.target.closest(this.selectors.modalClose) ||
        (e.target.closest(this.selectors.modal) && !e.target.closest(this.selectors.modalInner))
      ) {
        this.closeModal();
      }
    });

    document.addEventListener('click', e => {
      if (e.target.closest(this.selectors.countryItem)) {
        e.preventDefault();
        this.currentCountry = e.target.dataset.countryCode;
        this.updateLocalization(e.target.dataset.countryCode);
      }
    });

    document.addEventListener('input', e => {
      if (e.target.closest(this.selectors.search)) {
        const countryList = this.getAllCountry().filter(country =>
          country.name.toLowerCase().includes(e.target.value.toLowerCase()),
        );
        const countryListTemp = document.querySelector(this.selectors.countryList);

        if (countryListTemp) {
          countryListTemp.innerHTML = this.createCountryListTemp(countryList);
        }
      }
    });
  }

  init() {
    Shopify.currentCountry = this.currentCountry;
    this.selectedCountry.innerHTML = this.currentCountry.name;

    if (Shopify.plugs) {
      const plugs = Shopify.plugs.split(';');
      plugs.some(plug => {
        const regExp = /\(([^)]+)\)/;
        const matches = regExp.exec(plug);

        if (
          Array.isArray(matches) &&
          Shopify.currentCountry.plug &&
          matches[1].toLowerCase() === Shopify.currentCountry.plug.toLowerCase()
        ) {
          Shopify.currentCountry.plug = plug.trim();
          return true;
        }
      });
    }

    document.dispatchEvent(new CustomEvent('countrySelector:ready'));
  }

  // The visitor's country comes from Shopify's own IP lookup rather than
  // MaxMind. It still waits for ez_load - the first scroll, mousemove or
  // touchstart - because setCountry submits the localization form and reloads
  // the page, and that reload should not land on a visitor who hasn't
  // interacted yet (or on a Lighthouse run).
  countryIdentification() {
    document.addEventListener('ez_load', () => this.detectCountry(), {once: true});
  }

  async detectCountry() {
    try {
      const response = await fetch('/browsing_context_suggestions.json');

      if (!response.ok) {
        throw new Error(`browsing_context_suggestions responded ${response.status}`);
      }

      const {detected_values: detected} = await response.json();
      this.setCountry(detected?.country?.handle);
    } catch (error) {
      console.warn('Country detection failed, falling back to US:', error);
      this.setCountry(this.fallbackCountryCode);
    }
  }

  setCountry = countryCode => {
    const validCountryCode = this.getAllCountry().some(country => country.code === countryCode)
      ? countryCode
      : this.fallbackCountryCode;

    this.currentCountry = validCountryCode;
    this.updateLocalization(validCountryCode);
  };

  get currentCountry() {
    const countryCode = localStorage.getItem('country-iso-code');
    const countries = this.getAllCountry();
    return countries.find(country => country.code === countryCode) || {};
  }

  set currentCountry(country) {
    localStorage.setItem('country-iso-code', country);
  }

  createModal() {
    const modal = document.createElement('div');
    modal.innerHTML = `
            <div class="country-selector__modal">
              <div class="country-selector__modal-inner">
                <header class="country-selector__modal-header">
                  <div class="country-selector__modal-title">Select your shipping country</div>
                  <a href="#" class="country-selector__modal-close" aria-label="Close modal"></a>
                </header>
        
                <main class="country-selector__modal-main">
                  <input type="text" class="country-selector__modal-search" placeholder="Your country">
                  <ul class="country-selector__modal-country-list list-unstyled">
                    ${this.createCountryListTemp()}
                  </ul>
                </main>
        
                <footer class="country-selector__modal-footer">
                  <div class="country-selector__modal-delivery">Worldwide delivery</div>
                  <div class="country-selector__modal-shipping">Free shipping</div>
                </footer>
              </div>
            </div>
        `;

    return modal;
  }

  createCountryListTemp(countryList = this.getAllCountry()) {
    let countryListTemp = '';

    countryList.forEach(country => {
      let classes = 'country-selector__modal-country';
      if (country.code === this.currentCountry.code) {
        classes += ' active';
      }
      countryListTemp += `
        <li class="${classes}">
          <a href="#" data-country-code="${country.code}" aria-label="${country.name}">${country.name}</a>
        </li>
      `;
    });

    return countryListTemp ? countryListTemp : '<li class="country-selector__modal-country">Not found</li>';
  }

  openModal() {
    if (this.modal) {
      return;
    }

    this.modal = this.createModal();
    document.body.append(this.modal);

    if (!isIOS) {
      disableBodyScroll(this.modal);
    }
  }

  closeModal() {
    if (this.modal) {
      if (typeof enableBodyScroll === 'function') {
        enableBodyScroll(this.modal);
      }

      this.modal.remove();
      this.modal = null;
    }
  }

  updateLocalization(countryCode) {
    if (!this.countryForm) return;
    this.countryFormSelect.value = countryCode;
    this.countryForm.submit();
  }

  getAllCountry() {
    const {v110, v220} = Shopify.strings;

    const countries = [
      {
        name: 'Afghanistan',
        currency_code: 'AFN',
        plug: 'E',
        voltage: v220,
        code: 'AF',
      },
      {
        name: 'Albania',
        currency_code: 'ALL',
        plug: 'E',
        voltage: v220,
        code: 'AL',
      },
      {
        name: 'Algeria',
        currency_code: 'DZD',
        plug: 'E',
        voltage: v220,
        code: 'DZ',
      },
      {
        name: 'American Samoa',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'AS',
      },
      {
        name: 'Andorra',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'AD',
      },
      {
        name: 'Angola',
        currency_code: 'AOA',
        plug: 'E',
        voltage: v220,
        code: 'AO',
      },
      {
        name: 'Anguilla',
        currency_code: 'XCD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'AI',
      },
      {
        name: 'Antarctica',
        currency_code: 'XCD',
        plug: null,
        voltage: v220,
        code: 'AQ',
      },
      {
        name: 'Antigua and Barbuda',
        currency_code: 'XCD',
        plug: 'B',
        voltage: v220,
        code: 'AG',
      },
      {
        name: 'Argentina',
        currency_code: 'ARS',
        plug: 'I',
        voltage: v220,
        code: 'AR',
      },
      {
        name: 'Armenia',
        currency_code: 'AMD',
        plug: 'E',
        voltage: v220,
        code: 'AM',
      },
      {
        name: 'Aruba',
        currency_code: 'AWG',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'AW',
      },
      {
        name: 'Australia',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'AU',
      },
      {
        name: 'Austria',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'AT',
      },
      {
        name: 'Azerbaijan',
        currency_code: 'AZN',
        plug: 'E',
        voltage: v220,
        code: 'AZ',
      },
      {
        name: 'Bahamas',
        currency_code: 'BSD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'BS',
      },
      {
        name: 'Bahrain',
        currency_code: 'BHD',
        plug: 'G',
        voltage: v220,
        code: 'BH',
      },
      {
        name: 'Bangladesh',
        currency_code: 'BDT',
        plug: 'E',
        voltage: v220,
        code: 'BD',
      },
      {
        name: 'Barbados',
        currency_code: 'BBD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'BB',
      },
      {
        name: 'Belarus',
        currency_code: 'BYR',
        plug: 'E',
        voltage: v220,
        code: 'BY',
      },
      {
        name: 'Belgium',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'BE',
      },
      {
        name: 'Belize',
        currency_code: 'BZD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'BZ',
      },
      {
        name: 'Benin',
        currency_code: 'XOF',
        plug: 'E',
        voltage: v220,
        code: 'BJ',
      },
      {
        name: 'Bermuda',
        currency_code: 'BMD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'BM',
      },
      {
        name: 'Bhutan',
        currency_code: 'BTN',
        plug: 'E',
        voltage: v220,
        code: 'BT',
      },
      {
        name: 'Bolivia',
        currency_code: 'BOB',
        plug: 'B',
        voltage: v220,
        code: 'BO',
      },
      {
        name: 'Bosnia and Herzegovina',
        currency_code: 'BAM',
        plug: null,
        voltage: v220,
        code: 'BA',
      },
      {
        name: 'Botswana',
        currency_code: 'BWP',
        plug: 'G',
        voltage: v220,
        code: 'BW',
      },
      {
        name: 'Bouvet Island',
        currency_code: 'NOK',
        plug: null,
        voltage: v220,
        code: 'BV',
      },
      {
        name: 'Brazil',
        currency_code: 'BRL',
        plug: 'E',
        voltage: v220,
        code: 'BR',
      },
      {
        name: 'British Indian Ocean Territory',
        currency_code: 'USD',
        plug: null,
        voltage: v220,
        code: 'IO',
      },
      {
        name: 'Brunei',
        currency_code: 'BND',
        plug: 'G',
        voltage: v220,
        code: 'BN',
      },
      {
        name: 'Bulgaria',
        currency_code: 'BGN',
        plug: 'E',
        voltage: v220,
        code: 'BG',
      },
      {
        name: 'Burkina Faso',
        currency_code: 'XOF',
        plug: 'E',
        voltage: v220,
        code: 'BF',
      },
      {
        name: 'Burundi',
        currency_code: 'BIF',
        plug: 'E',
        voltage: v220,
        code: 'BI',
      },
      {
        name: 'Cambodia',
        currency_code: 'KHR',
        plug: 'B',
        voltage: v220,
        code: 'KH',
      },
      {
        name: 'Cameroon',
        currency_code: 'XAF',
        plug: 'E',
        voltage: v220,
        code: 'CM',
      },
      {
        name: 'Canada',
        currency_code: 'CAD',
        plug: 'B',
        voltage: v110,
        code: 'CA',
      },
      {
        name: 'Cape Verde',
        currency_code: 'CVE',
        plug: null,
        voltage: v220,
        code: 'CV',
      },
      {
        name: 'Cayman Islands',
        currency_code: 'KYD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'KY',
      },
      {
        name: 'Central African Republic',
        currency_code: 'XAF',
        plug: 'E',
        voltage: v220,
        code: 'CF',
      },
      {
        name: 'Chad',
        currency_code: 'XAF',
        plug: 'E',
        voltage: v220,
        code: 'TD',
      },
      {
        name: 'Chile',
        currency_code: 'CLP',
        plug: 'E',
        voltage: v220,
        code: 'CL',
      },
      {
        name: 'China',
        currency_code: 'CNY',
        plug: null,
        voltage: v220,
        code: 'CN',
      },
      {
        name: 'Christmas Island',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'CX',
      },
      {
        name: 'Cocos (Keeling) Islands',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'CC',
      },
      {
        name: 'Colombia',
        currency_code: 'COP',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'CO',
      },
      {
        name: 'Comoros',
        currency_code: 'KMF',
        plug: 'E',
        voltage: v220,
        code: 'KM',
      },
      {
        name: 'Congo',
        currency_code: 'XAF',
        plug: null,
        voltage: v220,
        code: 'CG',
      },
      {
        name: 'Cook Islands',
        currency_code: 'NZD',
        plug: 'I',
        voltage: v220,
        code: 'CK',
      },
      {
        name: 'Costa Rica',
        currency_code: 'CRC',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'CR',
      },
      {
        name: 'Croatia',
        currency_code: 'HRK',
        plug: 'E',
        voltage: v220,
        code: 'HR',
      },
      {
        name: 'Cuba',
        currency_code: 'CUP',
        plug: 'E',
        voltage: v220,
        code: 'CU',
      },
      {
        name: 'Cyprus',
        currency_code: 'EUR',
        plug: 'G',
        voltage: v220,
        code: 'CY',
      },
      {
        name: 'Czech Republic',
        currency_code: 'CZK',
        plug: null,
        voltage: v220,
        code: 'CZ',
      },
      {
        name: 'Denmark',
        currency_code: 'DKK',
        plug: 'K',
        voltage: v220,
        code: 'DK',
      },
      {
        name: 'Djibouti',
        currency_code: 'DJF',
        plug: 'E',
        voltage: v220,
        code: 'DJ',
      },
      {
        name: 'Dominica',
        currency_code: 'XCD',
        plug: 'G',
        voltage: v220,
        code: 'DM',
      },
      {
        name: 'Dominican Republic',
        currency_code: 'DOP',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'DO',
      },
      {
        name: 'Ecuador',
        currency_code: 'ECS',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'EC',
      },
      {
        name: 'Egypt',
        currency_code: 'EGP',
        plug: 'E',
        voltage: v220,
        code: 'EG',
      },
      {
        name: 'El Salvador',
        currency_code: 'SVC',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'SV',
      },
      {
        name: 'England',
        currency_code: 'GBP',
        plug: 'G',
        voltage: v220,
        code: 'GB',
      },
      {
        name: 'Equatorial Guinea',
        currency_code: 'XAF',
        plug: 'E',
        voltage: v220,
        code: 'GQ',
      },
      {
        name: 'Eritrea',
        currency_code: 'ERN',
        plug: 'E',
        voltage: v220,
        code: 'ER',
      },
      {
        name: 'Estonia',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'EE',
      },
      {
        name: 'Ethiopia',
        currency_code: 'ETB',
        plug: 'E',
        voltage: v220,
        code: 'ET',
      },
      {
        name: 'Falkland Islands',
        currency_code: 'FKP',
        plug: 'G',
        voltage: v220,
        code: 'FK',
      },
      {
        name: 'Faroe Islands',
        currency_code: 'DKK',
        plug: null,
        voltage: v220,
        code: 'FO',
      },
      {
        name: 'Fiji Islands',
        currency_code: 'FJD',
        plug: null,
        voltage: v220,
        code: 'FJ',
      },
      {
        name: 'Finland',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'FI',
      },
      {
        name: 'France',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'FR',
      },
      {
        name: 'French Guiana',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'GF',
      },
      {
        name: 'French Polynesia',
        currency_code: 'XPF',
        plug: null,
        voltage: v220,
        code: 'PF',
      },
      {
        name: 'French Southern territories',
        currency_code: 'EUR',
        plug: null,
        voltage: v220,
        code: 'TF',
      },
      {
        name: 'Gabon',
        currency_code: 'XAF',
        plug: null,
        voltage: v220,
        code: 'GA',
      },
      {
        name: 'Gambia',
        currency_code: 'GMD',
        plug: 'G',
        voltage: v220,
        code: 'GM',
      },
      {
        name: 'Georgia',
        currency_code: 'GEL',
        plug: 'E',
        voltage: v220,
        code: 'GE',
      },
      {
        name: 'Germany',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'DE',
      },
      {
        name: 'Ghana',
        currency_code: 'GHS',
        plug: 'G',
        voltage: v220,
        code: 'GH',
      },
      {
        name: 'Gibraltar',
        currency_code: 'GIP',
        plug: 'G',
        voltage: v220,
        code: 'GI',
      },
      {
        name: 'Greece',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'GR',
      },
      {
        name: 'Greenland',
        currency_code: 'DKK',
        plug: 'K',
        voltage: v220,
        code: 'GL',
      },
      {
        name: 'Grenada',
        currency_code: 'XCD',
        plug: 'G',
        voltage: v220,
        code: 'GD',
      },
      {
        name: 'Guadeloupe',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'GP',
      },
      {
        name: 'Guam',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'GU',
      },
      {
        name: 'Guatemala',
        currency_code: 'QTQ',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'GT',
      },
      {
        name: 'Guinea',
        currency_code: 'GNF',
        plug: 'E',
        voltage: v220,
        code: 'GN',
      },
      {
        name: 'Guinea-Bissau',
        currency_code: 'CFA',
        plug: 'E',
        voltage: v220,
        code: 'GW',
      },
      {
        name: 'Guyana',
        currency_code: 'GYD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'GY',
      },
      {
        name: 'Haiti',
        currency_code: 'HTG',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'HT',
      },
      {
        name: 'Heard Island and McDonald Islands',
        currency_code: 'AUD',
        plug: null,
        voltage: v220,
        code: 'HM',
      },
      {
        name: 'Holy See (Vatican City State)',
        currency_code: 'EUR',
        plug: null,
        voltage: v220,
        code: 'VA',
      },
      {
        name: 'Honduras',
        currency_code: 'HNL',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'HN',
      },
      {
        name: 'Hong Kong',
        currency_code: 'HKD',
        plug: 'G',
        voltage: v220,
        code: 'HK',
      },
      {
        name: 'Hungary',
        currency_code: 'HUF',
        plug: 'E',
        voltage: v220,
        code: 'HU',
      },
      {
        name: 'Iceland',
        currency_code: 'ISK',
        plug: 'E',
        voltage: v220,
        code: 'IS',
      },
      {
        name: 'India',
        currency_code: 'INR',
        plug: 'E',
        voltage: v220,
        code: 'IN',
      },
      {
        name: 'Indonesia',
        currency_code: 'IDR',
        plug: 'E',
        voltage: v220,
        code: 'ID',
      },
      {
        name: 'Iran',
        currency_code: 'IRR',
        plug: 'E',
        voltage: v220,
        code: 'IR',
      },
      {
        name: 'Iraq',
        currency_code: 'IQD',
        plug: 'E',
        voltage: v220,
        code: 'IQ',
      },
      {
        name: 'Ireland',
        currency_code: 'EUR',
        plug: null,
        voltage: v220,
        code: 'IE',
      },
      {
        name: 'Israel',
        currency_code: 'ILS',
        plug: 'E',
        voltage: v220,
        code: 'IL',
      },
      {
        name: 'Italy',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'IT',
      },
      {
        name: 'Ivory Coast',
        currency_code: 'XOF',
        plug: null,
        voltage: v220,
        code: 'CI',
      },
      {
        name: 'Jamaica',
        currency_code: 'JMD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'JM',
      },
      {
        name: 'Japan',
        currency_code: 'JPY',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'JP',
      },
      {
        name: 'Jordan',
        currency_code: 'JOD',
        plug: 'E',
        voltage: v220,
        code: 'JO',
      },
      {
        name: 'Kazakhstan',
        currency_code: 'KZT',
        plug: 'E',
        voltage: v220,
        code: 'KZ',
      },
      {
        name: 'Kenya',
        currency_code: 'KES',
        plug: 'G',
        voltage: v220,
        code: 'KE',
      },
      {
        name: 'Kiribati',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'KI',
      },
      {
        name: 'Kuwait',
        currency_code: 'KWD',
        plug: 'G',
        voltage: v220,
        code: 'KW',
      },
      {
        name: 'Kyrgyzstan',
        currency_code: 'KGS',
        plug: 'E',
        voltage: v220,
        code: 'KG',
      },
      {
        name: 'Laos',
        currency_code: 'LAK',
        plug: 'E',
        voltage: v220,
        code: 'LA',
      },
      {
        name: 'Latvia',
        currency_code: 'LVL',
        plug: 'E',
        voltage: v220,
        code: 'LV',
      },
      {
        name: 'Lebanon',
        currency_code: 'LBP',
        plug: 'E',
        voltage: v220,
        code: 'LB',
      },
      {
        name: 'Lesotho',
        currency_code: 'LSL',
        plug: 'E',
        voltage: v220,
        code: 'LS',
      },
      {
        name: 'Liberia',
        currency_code: 'LRD',
        plug: 'E',
        voltage: v220,
        code: 'LR',
      },
      {
        name: 'Libyan Arab Jamahiriya',
        currency_code: 'LYD',
        plug: null,
        voltage: v220,
        code: 'LY',
      },
      {
        name: 'Liechtenstein',
        currency_code: 'CHF',
        plug: 'E',
        voltage: v220,
        code: 'LI',
      },
      {
        name: 'Lithuania',
        currency_code: 'LTL',
        plug: 'E',
        voltage: v220,
        code: 'LT',
      },
      {
        name: 'Luxembourg',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'LU',
      },
      {
        name: 'Macao',
        currency_code: 'MOP',
        plug: null,
        voltage: v220,
        code: 'MO',
      },
      {
        name: 'North Macedonia',
        currency_code: 'MKD',
        plug: 'E',
        voltage: v220,
        code: 'MK',
      },
      {
        name: 'Madagascar',
        currency_code: 'MGF',
        plug: 'E',
        voltage: v220,
        code: 'MG',
      },
      {
        name: 'Malawi',
        currency_code: 'MWK',
        plug: 'G',
        voltage: v220,
        code: 'MW',
      },
      {
        name: 'Malaysia',
        currency_code: 'MYR',
        plug: 'G',
        voltage: v220,
        code: 'MY',
      },
      {
        name: 'Maldives',
        currency_code: 'MVR',
        plug: 'E',
        voltage: v220,
        code: 'MV',
      },
      {
        name: 'Mali',
        currency_code: 'XOF',
        plug: 'E',
        voltage: v220,
        code: 'ML',
      },
      {
        name: 'Malta',
        currency_code: 'EUR',
        plug: 'G',
        voltage: v220,
        code: 'MT',
      },
      {
        name: 'Marshall Islands',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'MH',
      },
      {
        name: 'Martinique',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'MQ',
      },
      {
        name: 'Mauritania',
        currency_code: 'MRO',
        plug: 'E',
        voltage: v220,
        code: 'MR',
      },
      {
        name: 'Mauritius',
        currency_code: 'MUR',
        plug: 'C',
        voltage: v220,
        code: 'MU',
      },
      {
        name: 'Mayotte',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'YT',
      },
      {
        name: 'Mexico',
        currency_code: 'MXN',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'MX',
      },
      {
        name: 'Micronesia, Federated States of',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'FM',
      },
      {
        name: 'Moldova',
        currency_code: 'MDL',
        plug: 'E',
        voltage: v220,
        code: 'MD',
      },
      {
        name: 'Monaco',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'MC',
      },
      {
        name: 'Mongolia',
        currency_code: 'MNT',
        plug: 'E',
        voltage: v220,
        code: 'MN',
      },
      {
        name: 'Montserrat',
        currency_code: 'XCD',
        plug: 'B',
        voltage: v220,
        code: 'MS',
      },
      {
        name: 'Morocco',
        currency_code: 'MAD',
        plug: 'E',
        voltage: v220,
        code: 'MA',
      },
      {
        name: 'Mozambique',
        currency_code: 'MZN',
        plug: 'E',
        voltage: v220,
        code: 'MZ',
      },
      {
        name: 'Myanmar',
        currency_code: 'MMR',
        plug: null,
        voltage: v220,
        code: 'MM',
      },
      {
        name: 'Namibia',
        currency_code: 'NAD',
        plug: 'E',
        voltage: v220,
        code: 'NA',
      },
      {
        name: 'Nauru',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'NR',
      },
      {
        name: 'Nepal',
        currency_code: 'NPR',
        plug: 'E',
        voltage: v220,
        code: 'NP',
      },
      {
        name: 'Netherlands',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'NL',
      },
      {
        name: 'Netherlands Antilles',
        currency_code: 'ANG',
        plug: null,
        voltage: v220,
        code: 'AN',
      },
      {
        name: 'New Caledonia',
        currency_code: 'XPF',
        plug: 'E',
        voltage: v220,
        code: 'NC',
      },
      {
        name: 'New Zealand',
        currency_code: 'NZD',
        plug: 'I',
        voltage: v220,
        code: 'NZ',
      },
      {
        name: 'Nicaragua',
        currency_code: 'NIO',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'NI',
      },
      {
        name: 'Niger',
        currency_code: 'XOF',
        plug: 'E',
        voltage: v220,
        code: 'NE',
      },
      {
        name: 'Nigeria',
        currency_code: 'NGN',
        plug: 'G',
        voltage: v220,
        code: 'NG',
      },
      {
        name: 'Niue',
        currency_code: 'NZD',
        plug: 'I',
        voltage: v220,
        code: 'NU',
      },
      {
        name: 'Norfolk Island',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'NF',
      },
      {
        name: 'North Korea',
        currency_code: 'KPW',
        plug: 'E',
        voltage: v220,
        code: 'KP',
      },
      {
        name: 'Northern Ireland',
        currency_code: 'GBP',
        plug: 'G',
        voltage: v220,
        code: 'GB',
      },
      {
        name: 'Northern Mariana Islands',
        currency_code: 'USD',
        plug: null,
        voltage: v220,
        code: 'MP',
      },
      {
        name: 'Norway',
        currency_code: 'NOK',
        plug: 'E',
        voltage: v220,
        code: 'NO',
      },
      {
        name: 'Oman',
        currency_code: 'OMR',
        plug: 'G',
        voltage: v220,
        code: 'OM',
      },
      {
        name: 'Pakistan',
        currency_code: 'PKR',
        plug: 'E',
        voltage: v220,
        code: 'PK',
      },
      {
        name: 'Palau',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'PW',
      },
      {
        name: 'Palestine',
        currency_code: null,
        plug: 'E',
        voltage: v220,
        code: 'PS',
      },
      {
        name: 'Panama',
        currency_code: 'PAB',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'PA',
      },
      {
        name: 'Papua New Guinea',
        currency_code: 'PGK',
        plug: 'I',
        voltage: v220,
        code: 'PG',
      },
      {
        name: 'Paraguay',
        currency_code: 'PYG',
        plug: 'C',
        voltage: v220,
        code: 'PY',
      },
      {
        name: 'Peru',
        currency_code: 'PEN',
        plug: 'E',
        voltage: v220,
        code: 'PE',
      },
      {
        name: 'Philippines',
        currency_code: 'PHP',
        plug: 'B',
        voltage: v220,
        code: 'PH',
      },
      {
        name: 'Pitcairn',
        currency_code: 'NZD',
        plug: null,
        voltage: v220,
        code: 'PN',
      },
      {
        name: 'Poland',
        currency_code: 'PLN',
        plug: 'E',
        voltage: v220,
        code: 'PL',
      },
      {
        name: 'Portugal',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'PT',
      },
      {
        name: 'Puerto Rico',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'PR',
      },
      {
        name: 'Qatar',
        currency_code: 'QAR',
        plug: 'G',
        voltage: v220,
        code: 'QA',
      },
      {
        name: 'Reunion',
        currency_code: 'EUR',
        plug: null,
        voltage: v220,
        code: 'RE',
      },
      {
        name: 'Romania',
        currency_code: 'RON',
        plug: 'E',
        voltage: v220,
        code: 'RO',
      },
      {
        name: 'Russian Federation',
        currency_code: 'RUB',
        plug: 'E',
        voltage: v220,
        code: 'RU',
      },
      {
        name: 'Rwanda',
        currency_code: 'RWF',
        plug: 'E',
        voltage: v220,
        code: 'RW',
      },
      {
        name: 'Saint Helena',
        currency_code: 'SHP',
        plug: 'G',
        voltage: v220,
        code: 'SH',
      },
      {
        name: 'Saint Kitts and Nevis',
        currency_code: 'XCD',
        plug: null,
        voltage: v220,
        code: 'KN',
      },
      {
        name: 'Saint Lucia',
        currency_code: 'XCD',
        plug: 'G',
        voltage: v220,
        code: 'LC',
      },
      {
        name: 'Saint Pierre and Miquelon',
        currency_code: 'EUR',
        plug: null,
        voltage: v220,
        code: 'PM',
      },
      {
        name: 'Saint Vincent and the Grenadines',
        currency_code: 'XCD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'VC',
      },
      {
        name: 'Samoa',
        currency_code: 'WST',
        plug: 'I',
        voltage: v220,
        code: 'WS',
      },
      {
        name: 'San Marino',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'SM',
      },
      {
        name: 'Sao Tome and Principe',
        currency_code: 'STD',
        plug: null,
        voltage: v220,
        code: 'ST',
      },
      {
        name: 'Saudi Arabia',
        currency_code: 'SAR',
        plug: 'G',
        voltage: v220,
        code: 'SA',
      },
      {
        name: 'Scotland',
        currency_code: 'GBP',
        plug: 'G',
        voltage: v220,
        code: 'GB',
      },
      {
        name: 'Senegal',
        currency_code: 'XOF',
        plug: 'E',
        voltage: v220,
        code: 'SN',
      },
      {
        name: 'Seychelles',
        currency_code: 'SCR',
        plug: 'G',
        voltage: v220,
        code: 'SC',
      },
      {
        name: 'Sierra Leone',
        currency_code: 'SLL',
        plug: 'G',
        voltage: v220,
        code: 'SL',
      },
      {
        name: 'Singapore',
        currency_code: 'SGD',
        plug: 'G',
        voltage: v220,
        code: 'SG',
      },
      {
        name: 'Slovakia',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'SK',
      },
      {
        name: 'Slovenia',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'SI',
      },
      {
        name: 'Solomon Islands',
        currency_code: 'SBD',
        plug: 'G',
        voltage: v220,
        code: 'SB',
      },
      {
        name: 'Somalia',
        currency_code: 'SOS',
        plug: 'G',
        voltage: v220,
        code: 'SO',
      },
      {
        name: 'South Africa',
        currency_code: 'ZAR',
        plug: 'E',
        voltage: v220,
        code: 'ZA',
      },
      {
        name: 'South Korea',
        currency_code: 'KRW',
        plug: 'F',
        voltage: v220,
        code: 'KR',
      },
      {
        name: 'South Sudan',
        currency_code: 'SSP',
        plug: 'E',
        voltage: v220,
        code: 'SS',
      },
      {
        name: 'Spain',
        currency_code: 'EUR',
        plug: 'E',
        voltage: v220,
        code: 'ES',
      },
      {
        name: 'Sri Lanka',
        currency_code: 'LKR',
        plug: null,
        voltage: v220,
        code: 'LK',
      },
      {
        name: 'Sudan',
        currency_code: 'SDG',
        plug: 'E',
        voltage: v220,
        code: 'SD',
      },
      {
        name: 'Suriname',
        currency_code: 'SRD',
        plug: 'E',
        voltage: v220,
        code: 'SR',
      },
      {
        name: 'Svalbard and Jan Mayen',
        currency_code: 'NOK',
        plug: null,
        voltage: v220,
        code: 'SJ',
      },
      {
        name: 'Swaziland',
        currency_code: 'SZL',
        plug: 'E',
        voltage: v220,
        code: 'SZ',
      },
      {
        name: 'Sweden',
        currency_code: 'SEK',
        plug: 'E',
        voltage: v220,
        code: 'SE',
      },
      {
        name: 'Switzerland',
        currency_code: 'CHF',
        plug: 'E',
        voltage: v220,
        code: 'CH',
      },
      {
        name: 'Syria',
        currency_code: 'SYP',
        plug: 'E',
        voltage: v220,
        code: 'SY',
      },
      {
        name: 'Tajikistan',
        currency_code: 'TJS',
        plug: 'E',
        voltage: v220,
        code: 'TJ',
      },
      {
        name: 'Tanzania',
        currency_code: 'TZS',
        plug: 'G',
        voltage: v220,
        code: 'TZ',
      },
      {
        name: 'Thailand',
        currency_code: 'THB',
        plug: 'B',
        voltage: v220,
        code: 'TH',
      },
      {
        name: 'The Democratic Republic of Congo',
        currency_code: 'CDF',
        plug: null,
        voltage: v220,
        code: 'CD',
      },
      {
        name: 'Togo',
        currency_code: 'XOF',
        plug: 'C',
        voltage: v220,
        code: 'TG',
      },
      {
        name: 'Tokelau',
        currency_code: 'NZD',
        plug: 'I',
        voltage: v220,
        code: 'TK',
      },
      {
        name: 'Tonga',
        currency_code: 'TOP',
        plug: 'I',
        voltage: v220,
        code: 'TO',
      },
      {
        name: 'Trinidad and Tobago',
        currency_code: 'TTD',
        plug: null,
        voltage: v220,
        code: 'TT',
      },
      {
        name: 'Tunisia',
        currency_code: 'TND',
        plug: 'E',
        voltage: v220,
        code: 'TN',
      },
      {
        name: 'Turkey',
        currency_code: 'TRY',
        plug: 'E',
        voltage: v220,
        code: 'TR',
      },
      {
        name: 'Turkmenistan',
        currency_code: 'TMT',
        plug: 'E',
        voltage: v220,
        code: 'TM',
      },
      {
        name: 'Turks and Caicos Islands',
        currency_code: 'USD',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'TC',
      },
      {
        name: 'Tuvalu',
        currency_code: 'AUD',
        plug: 'I',
        voltage: v220,
        code: 'TV',
      },
      {
        name: 'Uganda',
        currency_code: 'UGX',
        plug: 'G',
        voltage: v220,
        code: 'UG',
      },
      {
        name: 'Ukraine',
        currency_code: 'UAH',
        plug: 'E',
        voltage: v220,
        code: 'UA',
      },
      {
        name: 'United Arab Emirates',
        currency_code: 'AED',
        plug: null,
        voltage: v220,
        code: 'AE',
      },
      {
        name: 'United Kingdom',
        currency_code: 'GBP',
        plug: null,
        voltage: v220,
        code: 'GB',
      },
      {
        name: 'United States',
        currency_code: 'USD',
        plug: 'B',
        voltage: v110,
        code: 'US',
      },
      {
        name: 'United States Minor Outlying Islands',
        currency_code: 'USD',
        plug: null,
        voltage: v220,
        code: 'UM',
      },
      {
        name: 'Uruguay',
        currency_code: 'UYU',
        plug: 'E',
        voltage: v220,
        code: 'UY',
      },
      {
        name: 'Uzbekistan',
        currency_code: 'UZS',
        plug: 'E',
        voltage: v220,
        code: 'UZ',
      },
      {
        name: 'Vanuatu',
        currency_code: 'VUV',
        plug: 'I',
        voltage: v220,
        code: 'VU',
      },
      {
        name: 'Venezuela',
        currency_code: 'VEF',
        plug: 'B',
        //voltage: '110V (PLUS)',
        voltage: v110,
        code: 'VE',
      },
      {
        name: 'Vietnam',
        currency_code: 'VND',
        plug: 'B',
        voltage: v220,
        code: 'VN',
      },
      {
        name: 'Virgin Islands, British',
        currency_code: 'USD',
        plug: null,
        voltage: v220,
        code: 'VG',
      },
      {
        name: 'Virgin Islands, U.S.',
        currency_code: 'USD',
        plug: null,
        voltage: v220,
        code: 'VI',
      },
      {
        name: 'Wales',
        currency_code: 'GBP',
        plug: 'G',
        voltage: v220,
        code: 'GB',
      },
      {
        name: 'Wallis and Futuna',
        currency_code: 'XPF',
        plug: null,
        voltage: v220,
        code: 'WF',
      },
      {
        name: 'Western Sahara',
        currency_code: 'MAD',
        plug: 'E',
        voltage: v220,
        code: 'EH',
      },
      {
        name: 'Yemen',
        currency_code: 'YER',
        plug: 'E',
        voltage: v220,
        code: 'YE',
      },
      {
        name: 'Yugoslavia',
        currency_code: null,
        plug: null,
        voltage: v220,
        code: 'YU',
      },
      {
        name: 'Zambia',
        currency_code: 'ZMW',
        plug: 'E',
        voltage: v220,
        code: 'ZM',
      },
      {
        name: 'Zimbabwe',
        currency_code: 'ZWD',
        plug: 'G',
        voltage: v220,
        code: 'ZW',
      },
    ];

    return this.availableCountries
      .map(countryCode => countries.find(country => country.code === countryCode))
      .filter(Boolean);
  }
}

customElements.define('country-selector', CountrySelector);
